/**
 * Copilot dispatch — single entry point that takes a `ProposalApply`
 * payload, looks up the matching tool in `COPILOT_TOOLS`, validates +
 * applies it against the right store, records an audit entry, and
 * returns a structured result the UI can render.
 *
 * Two-step flow:
 *
 *   1. `validateProposal(apply, stores)` — pure, runs at *render time*
 *      so a bad proposal card shows red with a reason and disabled
 *      Accept. No state mutation.
 *
 *   2. `applyProposal(apply, stores, opts)` — runs on click. Mutates
 *      the store + records audit + returns { ok, summary, audit?, undoData? }.
 *
 * The legacy plant-graph variants (`add_entity`, `add_connection`,
 * `add_computed_tag`) are dispatched here too — they hit the plant +
 * compute stores directly instead of the tool registry, because those
 * tools predate the registry. Future refactor: lift them into
 * `COPILOT_TOOLS` so the dispatcher is uniform.
 */

import type { ProposalApply } from "@/lib/widgets/__foundry_fixtures";
import {
  applyTool,
  validateTool,
  type CopilotToolPayload,
  type ToolStores,
  type ValidationResult,
} from "@/lib/copilot/tools";
import { recordAudit, type AuditEntry } from "@/lib/copilot/audit";
import type { PlantState } from "@/lib/plant/plant-store";
import type { ComputedTagsState } from "@/lib/compute/store";
import { findTemplate, type DataKind } from "@foundry/foundry-plant";

/** Stores the dispatcher needs — superset of `ToolStores`. */
export interface DispatchStores extends ToolStores {
  plant: PlantState;
  computed: ComputedTagsState;
}

export interface DispatchResult {
  ok: boolean;
  reason?: string;
  summary?: string;
  audit?: AuditEntry;
}

/** Pure validator — never mutates. Returns ok=true for plant-graph variants. */
export function validateProposal(
  apply: ProposalApply,
  stores: DispatchStores,
): ValidationResult {
  // Plant-graph proposals — pre-registry, always considered valid.
  if (
    apply.type === "add_entity" ||
    apply.type === "add_connection" ||
    apply.type === "add_computed_tag"
  ) {
    if (apply.type === "add_entity") {
      const template = findTemplate(apply.templateId);
      if (!template) {
        return {
          ok: false,
          reason: `Template "${apply.templateId}" not found in the pack catalogue.`,
        };
      }
    }
    return { ok: true };
  }
  // Registry-backed tools — same `type` discriminator works directly.
  return validateTool(apply as CopilotToolPayload, stores);
}

/** Apply a proposal payload. Records an audit entry on success. */
export function applyProposal(
  apply: ProposalApply,
  stores: DispatchStores,
  opts: {
    proposalId?: string;
    aliasMap?: Record<string, string>;
    onAlias?: (placeholder: string, realId: string) => void;
  } = {},
): DispatchResult {
  // ── Plant-graph tools (pre-registry) ───────────────────────────────
  if (apply.type === "add_entity") {
    const template = findTemplate(apply.templateId);
    if (!template) {
      return { ok: false, reason: `Template "${apply.templateId}" not found.` };
    }
    const placeholderId = `n_new_${apply.templateId.split(".").pop()}`;
    const realId = stores.plant.addEntity(template, apply.instanceName);
    opts.onAlias?.(placeholderId, realId);
    const summary = `Added ${apply.instanceName} to the plant model`;
    const audit = recordAudit(
      "add_entity",
      apply,
      { entityId: realId },
      summary,
      opts.proposalId,
    );
    return { ok: true, summary, audit };
  }

  if (apply.type === "add_connection") {
    const source = opts.aliasMap?.[apply.source] ?? apply.source;
    const target = opts.aliasMap?.[apply.target] ?? apply.target;
    const sourceExists = stores.plant.nodes.some((n) => n.id === source);
    const targetExists = stores.plant.nodes.some((n) => n.id === target);
    if (!sourceExists || !targetExists) {
      return {
        ok: false,
        reason: `Source or target node not in the model (${source} → ${target}).`,
      };
    }
    stores.plant.addEdge({
      source,
      target,
      sourceHandle: apply.sourceHandle,
      targetHandle: apply.targetHandle,
      label: apply.label,
      materialClass: apply.materialClass as never,
    });
    const summary = `Connected ${source} → ${target}`;
    const audit = recordAudit(
      "add_connection",
      apply,
      { source, target },
      summary,
      opts.proposalId,
    );
    return { ok: true, summary, audit };
  }

  if (apply.type === "add_computed_tag") {
    const def = apply.def;
    const id = stores.computed.add({
      name: def.name,
      description: def.description,
      outputUnit: def.outputUnit,
      outputDataKind: def.outputDataKind as DataKind,
      inputs: def.inputs,
      expression: def.expression,
      trigger: def.trigger,
      enabled: true,
      presetId: def.presetId,
    });
    const summary = `Added computed tag "${def.name}"`;
    const audit = recordAudit(
      "add_computed_tag",
      apply,
      { computedTagId: id },
      summary,
      opts.proposalId,
    );
    return { ok: true, summary, audit };
  }

  // ── Registry tools — uniform dispatch ──────────────────────────────
  const result = applyTool(apply as CopilotToolPayload, stores);
  if (!result.ok) {
    return { ok: false, reason: result.reason };
  }
  const audit = recordAudit(
    apply.type,
    apply,
    result.undoData,
    result.summary ?? `Applied ${apply.type}`,
    opts.proposalId,
  );
  return { ok: true, summary: result.summary, audit };
}

/**
 * Undo a previously-applied audit entry. Returns true if the reversal
 * succeeded. Phase 0 covers the registry tools' `undoData` shapes;
 * plant-graph variants are best-effort.
 */
export function undoAuditEntry(
  entry: AuditEntry,
  stores: DispatchStores,
): { ok: boolean; reason?: string } {
  try {
    switch (entry.toolId) {
      case "create_dashboard": {
        const { dashboardId } = entry.undoData as { dashboardId: string };
        stores.dashboards.remove(dashboardId);
        return { ok: true };
      }
      case "clone_dashboard": {
        const { dashboardId } = entry.undoData as { dashboardId: string };
        stores.dashboards.remove(dashboardId);
        return { ok: true };
      }
      case "add_widget": {
        const { dashboardId, widgetId } = entry.undoData as {
          dashboardId: string;
          widgetId: string;
        };
        stores.dashboards.removeWidget(dashboardId, widgetId);
        return { ok: true };
      }
      case "bind_widget_slot": {
        const { dashboardId, widgetId, previousDataSource } = entry.undoData as {
          dashboardId: string;
          widgetId: string;
          previousDataSource?: import("@/lib/dashboard/types").DataSource;
        };
        if (previousDataSource) {
          stores.dashboards.setWidgetDataSource(
            dashboardId,
            widgetId,
            previousDataSource,
          );
        } else {
          // No prior binding — patch the widget to drop the dataSource.
          stores.dashboards.updateWidget(dashboardId, widgetId, {
            dataSource: undefined,
          });
        }
        return { ok: true };
      }
      case "set_widget_title": {
        const { dashboardId, widgetId, previousTitle, previousSubtitle } =
          entry.undoData as {
            dashboardId: string;
            widgetId: string;
            previousTitle?: string;
            previousSubtitle?: string;
          };
        stores.dashboards.updateWidget(dashboardId, widgetId, {
          title: previousTitle,
          subtitle: previousSubtitle,
        });
        return { ok: true };
      }
      case "delete_widget": {
        const { dashboardId, widget } = entry.undoData as {
          dashboardId: string;
          widget: import("@/lib/dashboard/types").WidgetInstance;
        };
        stores.dashboards.addWidget(dashboardId, widget);
        return { ok: true };
      }
      case "create_alarm_rule": {
        const { ruleId } = entry.undoData as { ruleId: string };
        stores.alarms.deleteRule(ruleId);
        return { ok: true };
      }
      case "update_alarm_rule": {
        const { ruleId, previous } = entry.undoData as {
          ruleId: string;
          previous: import("@/lib/alarms/store").AlarmRule;
        };
        stores.alarms.updateRule(ruleId, previous);
        return { ok: true };
      }
      case "delete_alarm_rule": {
        const { rule } = entry.undoData as {
          rule: import("@/lib/alarms/store").AlarmRule;
        };
        stores.alarms.createRule({
          name: rule.name,
          targetTagId: rule.targetTagId,
          targetLabel: rule.targetLabel,
          condition: rule.condition,
          severity: rule.severity,
          channels: rule.channels,
          enabled: rule.enabled,
          triggers7d: rule.triggers7d,
        });
        return { ok: true };
      }
      case "add_entity": {
        const { entityId } = entry.undoData as { entityId: string };
        stores.plant.removeNode(entityId);
        return { ok: true };
      }
      case "add_computed_tag": {
        const { computedTagId } = entry.undoData as { computedTagId: string };
        stores.computed.remove(computedTagId);
        return { ok: true };
      }
      default:
        return { ok: false, reason: `Undo for "${entry.toolId}" not implemented.` };
    }
  } catch (err) {
    return {
      ok: false,
      reason: err instanceof Error ? err.message : String(err),
    };
  }
}
